import { createFileRoute, redirect } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { CLAIM_CONTENT } from '../../features/auth/claimContent';
import { AuthCard } from '../../features/auth/components/AuthCard';
import { ClaimAccountForm } from '../../features/auth/components/ClaimAccountForm';
import { ensureIdentity } from '../../features/auth/useAuth';
import { PublicLayout } from '../../layouts/PublicLayout';
import { isLocale } from '../../lib/i18n';

/**
 * Guest → account conversion. Reachable only by a guest: someone with an account is sent to
 * their cabinet, and a true anonymous visitor to sign-up, since there is no guest history to
 * keep. The guard runs before the component so the wrong visitor never sees the form.
 */
export const Route = createFileRoute('/$lang/claim')({
  beforeLoad: async ({ context, params }) => {
    const identity = await ensureIdentity(context.queryClient);
    if (identity === null) {
      throw redirect({ to: '/$lang/signup', params: { lang: params.lang } });
    }
    if (!identity.isGuest) {
      throw redirect({ to: '/$lang/app', params: { lang: params.lang } });
    }
  },
  component: ClaimScreen,
});

function ClaimScreen(): ReactNode {
  const { lang } = Route.useParams();

  return (
    <PublicLayout lang={lang}>
      <AuthCard title={CLAIM_CONTENT.title} subtitle={CLAIM_CONTENT.subtitle}>
        <ClaimAccountForm lang={isLocale(lang) ? lang : 'en'} />
      </AuthCard>
    </PublicLayout>
  );
}
